'use client';

/**
 * Typed result cards for high-value CRM tools — entity chip lists that link
 * into Hub, and mutation receipts — instead of raw JSON.
 *
 * Rendering is strictly best-effort: every renderer validates the output
 * shape and returns null on anything unexpected, so the caller falls back to
 * the generic JSON view. TOON/plain-text outputs (strings) never match here.
 *
 * This file is the public entry point and stays a thin registry. The pieces
 * live in `./tool-result-cards/`:
 *  - `entity-kinds.ts`         — kinds, Hub routes, tool→kind maps
 *  - `entity-view.ts`          — pure projection of raw output → chip fields
 *  - `code-execution-view.ts`  — pure projection of a sandbox result
 *  - `EntityCards.tsx`         — chips + mutation receipt
 *  - `CodeExecutionResult.tsx` — sandbox output card
 */

import React from 'react';
import { LIST_TOOL_ENTITIES, MUTATION_TOOL_ENTITIES } from './tool-result-cards/entity-kinds.js';
import { buildEntityViews } from './tool-result-cards/entity-view.js';
import { buildCodeExecutionView } from './tool-result-cards/code-execution-view.js';
import { EntityChipList, MutationReceipt } from './tool-result-cards/EntityCards.js';
import { CodeExecutionResult } from './tool-result-cards/CodeExecutionResult.js';

export type { EntityKind } from './tool-result-cards/entity-kinds.js';
export type { EntityView } from './tool-result-cards/entity-view.js';
export type { CodeExecutionView } from './tool-result-cards/code-execution-view.js';

/**
 * Registry entry point: typed card for a tool result, or null when the tool
 * is unmapped or the output shape doesn't match (caller falls back to JSON).
 */
export function renderToolResultCard(toolName: string, output: unknown): React.ReactNode | null {
  if (output === undefined || output === null || typeof output === 'string') return null;

  if (toolName === 'code_execution') {
    const view = buildCodeExecutionView(output);
    return view ? <CodeExecutionResult view={view} /> : null;
  }

  const listKind = LIST_TOOL_ENTITIES[toolName];
  if (listKind) {
    const views = buildEntityViews(output, listKind);
    return views ? <EntityChipList views={views} kind={listKind} /> : null;
  }

  const mutationKind = MUTATION_TOOL_ENTITIES[toolName];
  if (mutationKind) {
    const views = buildEntityViews(output, mutationKind);
    return views ? <MutationReceipt view={views[0]!} kind={mutationKind} /> : null;
  }

  return null;
}
